import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  DevHubBox,
  DevHubTypography,
  DevHubButton,
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Application, ChangesSummaryItem } from '../types';
import {
  generateMutationInputs,
  generateSingleDeleteInputs,
} from '../utils/mutationUtils';
import { MutationStatus } from '../../AppBulkEdit/types/updateApplication/BaseApplicationProperty';
import {
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubTooltip,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import { Theme } from '@material-ui/core/styles';
import {
  CreateResponsibilitiesMutation,
  DeleteResponsibilityMutation,
} from '../queries';

const useStatusStyles = devhubMakeStyles((theme: Theme) => ({
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
}));

interface DataLeadSubmissionReviewProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (successfulApplicationIds: number[]) => void;
  changes: ChangesSummaryItem[];
  applications: Application[];
  functionalAreasData: any;
  selectedFunctionalArea: string;
  currentAssignments: Map<number, Set<string>>;
  onProcessingComplete?: (successfulApplicationIds: number[]) => void;
  onMutationStart?: () => void;
  onMutationEnd?: () => void;
}

interface SubmissionRowData {
  applicationId: number;
  applicationName: string;
  changesDescription: string;
  status: 'pending' | 'processing' | 'success' | 'error';
}

// Inline status component to avoid import issues
const StatusDisplay = ({
  applicationId,
  status,
  mutationStatus,
}: {
  applicationId: number;
  status: 'pending' | 'processing' | 'success' | 'error';
  mutationStatus: MutationStatus[];
}) => {
  const statusClasses = useStatusStyles();
  const mutationResult = mutationStatus.find(
    result => result.applicationId === applicationId,
  );

  const getTooltipMessage = (): string => {
    if (mutationResult) {
      return mutationResult.message;
    }

    switch (status) {
      case 'pending':
        return 'Waiting to be processed';
      case 'processing':
        return 'Processing data lead assignment changes...';
      case 'success':
        return 'Data lead assignments updated successfully';
      case 'error':
        return 'Failed to update data lead assignments';
      default:
        return '';
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'pending':
        return <span>⏳ Pending</span>;
      case 'processing':
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DevHubCircularProgress size={16} />
            <span>Processing</span>
          </span>
        );
      case 'success':
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DevHubDynamicIcon
              iconName="CheckCircle"
              className={statusClasses.successIcon}
            />
            <span>Success</span>
          </span>
        );
      case 'error':
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DevHubDynamicIcon
              iconName="Error"
              className={statusClasses.errorIcon}
            />
            <span>Failed</span>
          </span>
        );
      default:
        return <span>Unknown</span>;
    }
  };

  return (
    <DevHubTooltip title={getTooltipMessage()}>
      <span>{renderContent()}</span>
    </DevHubTooltip>
  );
};

/**
 * Component for showing submission status of data lead assignment changes.
 * This is the status dialog that appears after the user confirms changes in the review dialog.
 * @param props - Component props
 * @param props.open - Whether the dialog is open
 * @param props.onClose - Function to close the dialog
 * @param props.onConfirm - Function to confirm submission
 * @param props.changes - Array of changes to submit
 * @param props.applications - Array of applications
 * @param props.functionalAreasData - Functional areas data
 * @param props.selectedFunctionalArea - Currently selected functional area
 */
const DataLeadSubmissionReview = ({
  open,
  onClose,
  onConfirm,
  changes,
  applications,
  functionalAreasData,
  selectedFunctionalArea,
  currentAssignments,
  onProcessingComplete,
}: DataLeadSubmissionReviewProps): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mutationStatus, setMutationStatus] = useState<MutationStatus[]>([]);
  const [submissionStarted, setSubmissionStarted] = useState(false);

  // Keep a snapshot of changes when dialog opens to prevent clearing successful rows
  const [dialogChanges, setDialogChanges] = useState<ChangesSummaryItem[]>([]);

  // Apollo Client mutation hooks
  const [createResponsibilities] = useMutation(CreateResponsibilitiesMutation);
  const [deleteResponsibility] = useMutation(DeleteResponsibilityMutation);

  // Generate mutation inputs
  const mutationInputs = generateMutationInputs(
    dialogChanges,
    functionalAreasData,
    selectedFunctionalArea,
    applications,
    currentAssignments,
  );

  // Convert delete inputs to proper format with responsibilityId
  const { deleteInputs: singleDeleteInputs } = generateSingleDeleteInputs(
    mutationInputs.deleteInputs,
    applications,
  );

  // Prepare row data for the DataTable
  const getApplicationStatus = (
    applicationId: number,
  ): 'pending' | 'processing' | 'success' | 'error' => {
    if (!submissionStarted) return 'pending';

    const statusResult = mutationStatus.find(
      status => status.applicationId === applicationId,
    );
    if (statusResult) {
      return statusResult.success ? 'success' : 'error';
    }

    return isSubmitting ? 'processing' : 'pending';
  };

  const renderSubmissionData = (): SubmissionRowData[] => {
    return dialogChanges.map(change => {
      const changesText = change.changes
        .map(c => `${c.action} ${c.leadName}`)
        .join(', ');

      const status = getApplicationStatus(change.applicationId);

      return {
        applicationId: change.applicationId,
        applicationName: change.applicationName,
        changesDescription: changesText,
        status,
      };
    });
  };

  // Execute the submission process with real mutations
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionStarted(true);

    const results: MutationStatus[] = [];

    try {
      // Group ALL inputs by application ID - both creates and deletes
      const operationsByApp: Record<
        number,
        {
          creates: typeof mutationInputs.createInputs;
          deletes: typeof singleDeleteInputs;
        }
      > = {};

      // Group create inputs by application
      mutationInputs.createInputs.forEach(input => {
        if (!operationsByApp[input.applicationId]) {
          operationsByApp[input.applicationId] = { creates: [], deletes: [] };
        }
        operationsByApp[input.applicationId].creates.push(input);
      });

      // Group delete inputs by application (using the properly formatted ones)
      singleDeleteInputs.forEach(input => {
        if (!operationsByApp[input.applicationId]) {
          operationsByApp[input.applicationId] = { creates: [], deletes: [] };
        }
        operationsByApp[input.applicationId].deletes.push(input);
      });

      // Process all operations for each application (creates AND deletes)
      for (const [appId, operations] of Object.entries(operationsByApp)) {
        const applicationId = parseInt(appId, 10);
        let applicationSuccess = true;
        const messages: string[] = [];

        try {
          // Execute CREATE operations for this application
          if (operations.creates.length > 0) {
            // eslint-disable-next-line no-console -- High-level debugging for create operations timing in development
            console.log(
              `🔧 [${new Date().toISOString()}] Starting CREATE operations for application ${applicationId}`,
            );

            await createResponsibilities({
              variables: { input: operations.creates },
            });

            messages.push(
              `Created ${operations.creates.length} responsibilities`,
            );
            // eslint-disable-next-line no-console -- High-level debugging for create completion timing in development
            console.log(
              `✅ [${new Date().toISOString()}] CREATE operations completed for application ${applicationId}`,
            );
          }

          // Execute DELETE operations for this application
          if (operations.deletes.length > 0) {
            // eslint-disable-next-line no-console -- High-level debugging for delete operations timing in development
            console.log(
              `🗑️ [${new Date().toISOString()}] Starting DELETE operations for application ${applicationId}:`,
              operations.deletes,
            );

            // Delete operations need to be called individually using responsibilityId
            for (const deleteInput of operations.deletes) {
              // eslint-disable-next-line no-console -- High-level debugging for individual delete operations in development
              console.log(
                `🗑️ [${new Date().toISOString()}] Deleting responsibility ${deleteInput.responsibilityId} for application ${applicationId}`,
              );

              await deleteResponsibility({
                variables: { input: deleteInput },
              });
            }

            messages.push(
              `Deleted ${operations.deletes.length} responsibilities`,
            );
            // eslint-disable-next-line no-console -- High-level debugging for delete completion timing in development
            console.log(
              `✅ [${new Date().toISOString()}] DELETE operations completed for application ${applicationId}`,
            );
          }
        } catch (error) {
          applicationSuccess = false;
          messages.push(
            `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          );
          // eslint-disable-next-line no-console -- Error logging for application operations in development
          console.error(
            `❌ [${new Date().toISOString()}] Operations failed for application ${applicationId}:`,
            error,
          );
        }

        // Update results for this application
        const existingIndex = results.findIndex(
          r => r.applicationId === applicationId,
        );
        if (existingIndex >= 0) {
          results[existingIndex] = {
            ...results[existingIndex],
            success: applicationSuccess,
            message: messages.join(', '),
          };
        } else {
          results.push({
            applicationId,
            success: applicationSuccess,
            message: messages.join(', '),
          });
        }

        // Update UI with current progress
        setMutationStatus([...results]);

        // Only show checkmark and trigger refresh AFTER ALL operations for this application are complete
        if (applicationSuccess) {
          // eslint-disable-next-line no-console -- High-level debugging for application completion timing in development
          console.log(
            `✅ [${new Date().toISOString()}] Application ${applicationId} ALL operations completed - green checkmark should appear`,
          );

          // Trigger immediate refresh for this successful application
          if (onProcessingComplete) {
            // eslint-disable-next-line no-console -- High-level debugging for immediate refresh per application in development
            console.log(
              `🚀 [${new Date().toISOString()}] Immediately triggering refresh for application ${applicationId}`,
            );
            onProcessingComplete([applicationId]);
          }
        }
      }

      // All applications processed - log final summary
    } catch (error) {
      // Mark all remaining applications as failed
      changes.forEach(change => {
        if (!results.find(r => r.applicationId === change.applicationId)) {
          results.push({
            applicationId: change.applicationId,
            success: false,
            message: `Critical error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      });
      setMutationStatus([...results]);
    }

    setIsSubmitting(false);

    // All individual application refreshes have already been triggered during processing
    // eslint-disable-next-line no-console -- High-level debugging for final mutation results in development
    console.log(
      `🎯 [${new Date().toISOString()}] All mutations completed. Final results:`,
      results,
    );
  };

  // Capture changes snapshot when dialog opens
  useEffect(() => {
    if (open && changes.length > 0) {
      // eslint-disable-next-line no-console -- High-level debugging for dialog state changes in development
      console.log('📋 Dialog opened, capturing changes snapshot:', changes);
      setDialogChanges([...changes]); // Create a snapshot to prevent clearing successful rows
    }
  }, [open, changes]);

  // Reset state only when dialog opens (not on every change)
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line no-console -- High-level debugging for dialog state reset in development
      console.log('🔄 Dialog opened, resetting component state');
      setIsSubmitting(false);
      setMutationStatus([]);
      setSubmissionStarted(false);
    }
  }, [open]); // Only depend on open, not changes

  // Automatically start submission when dialog opens
  useEffect(() => {
    if (open && !submissionStarted && dialogChanges.length > 0) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submissionStarted, dialogChanges.length]);

  // Simplified completion check - just check if we have any mutation results
  const hasResults = mutationStatus.length > 0;
  const isProcessingComplete = hasResults && !isSubmitting;

  // Note: onProcessingComplete is now called immediately in handleSubmit when mutations finish
  // This ensures background refresh starts as soon as green checkmarks appear, not after a delay

  const handleClose = () => {
    // Prevent closing during processing - power user pattern
    if (isSubmitting) {
      // eslint-disable-next-line no-console -- High-level debugging for prevented close during processing
      console.log(
        `⛔ [${new Date().toISOString()}] Dialog close prevented - operations still in progress`,
      );
      return;
    }

    const closeTimestamp = new Date().toISOString();
    // eslint-disable-next-line no-console -- High-level debugging for dialog close operations in development
    console.log(
      `🚪 [${closeTimestamp}] Dialog closing, all operations complete`,
    );

    // Get successful application IDs to pass back
    const successfulApplicationIds = mutationStatus
      .filter(status => status.success)
      .map(status => status.applicationId);

    // Always trigger refresh if there were successful mutations
    if (successfulApplicationIds.length > 0) {
      // eslint-disable-next-line no-console -- High-level debugging for refresh trigger timing in development
      console.log(
        `🔄 [${new Date().toISOString()}] Dialog close triggering refresh for successful applications:`,
        successfulApplicationIds,
      );
      onConfirm(successfulApplicationIds);
      // eslint-disable-next-line no-console -- High-level debugging for dialog close callback completion in development
      console.log(
        `✅ [${new Date().toISOString()}] Dialog close onConfirm callback completed`,
      );
    } else if (onClose) {
      // eslint-disable-next-line no-console -- High-level debugging for normal dialog close in development
      console.log(
        `🚪 [${new Date().toISOString()}] No successful mutations, closing dialog normally`,
      );
      onClose();
    }

    // Reset state after triggering callbacks
    setIsSubmitting(false);
    setMutationStatus([]);
    setSubmissionStarted(false);
    setDialogChanges([]);
  };

  const hasErrors = mutationStatus.some(status => !status.success);

  const handleDialogClose = (_event: any, reason: string) => {
    // Prevent closing on backdrop click when submitting
    if (isSubmitting && reason === 'backdropClick') {
      return;
    }
    handleClose();
  };

  return (
    <DevHubDialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={isSubmitting}
      keepMounted={false}
      maxWidth="lg"
      fullWidth
    >
      <DevHubDialogTitle>
        <DevHubBox display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
          Data Lead Assignment Submission Status
          {isSubmitting && (
            <DevHubBox
              display="flex"
              alignItems="center"
              style={{ gap: '0.25rem' }}
            >
              <DevHubCircularProgress size={16} />
              <DevHubTypography variant="body2" color="textSecondary">
                Processing...
              </DevHubTypography>
            </DevHubBox>
          )}
        </DevHubBox>
      </DevHubDialogTitle>

      <DevHubDialogContent>
        <DevHubBox marginBottom="medium">
          <DevHubTypography variant="body1">
            {isSubmitting
              ? `Processing changes for ${dialogChanges.length} application${dialogChanges.length !== 1 ? 's' : ''}...`
              : `Completed processing ${dialogChanges.length} application${dialogChanges.length !== 1 ? 's' : ''}`}
          </DevHubTypography>
          {isSubmitting && (
            <DevHubTypography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: '0.5rem' }}
            >
              Please wait while operations complete. Dialog will remain open to
              ensure all changes are processed.
            </DevHubTypography>
          )}
          {!isSubmitting && submissionStarted && (
            <DevHubBox marginTop="medium">
              <DevHubTypography variant="body2" color="textSecondary">
                {(() => {
                  const successful = mutationStatus.filter(
                    s => s.success,
                  ).length;
                  const failed = mutationStatus.filter(s => !s.success).length;
                  const total = mutationStatus.length;

                  if (failed === 0) {
                    return `✅ All ${successful} applications processed successfully. Data refresh is in progress.`;
                  }
                  return `⚠️ ${successful} applications succeeded, ${failed} failed out of ${total} total.`;
                })()}
              </DevHubTypography>
            </DevHubBox>
          )}
        </DevHubBox>

        <DataTable
          value={renderSubmissionData()}
          paginator={false}
          dataKey="applicationId"
          className="p-datatable-sm"
        >
          <Column
            field="applicationName"
            header="Application"
            style={{ width: '250px' }}
          />
          <Column
            field="changesDescription"
            header="Changes"
            style={{ minWidth: '300px' }}
          />
          <Column
            field="status"
            header="Status"
            style={{ width: '150px' }}
            body={(rowData: SubmissionRowData) => (
              <StatusDisplay
                applicationId={rowData.applicationId}
                status={rowData.status}
                mutationStatus={mutationStatus}
              />
            )}
          />
        </DataTable>

        {submissionStarted && (
          <DevHubBox marginTop="medium">
            <DevHubTypography variant="body2" color="textSecondary">
              {(() => {
                if (isSubmitting) {
                  return `Processing ${mutationStatus.length}/${dialogChanges.length} applications...`;
                }
                if (isProcessingComplete) {
                  return `Completed ${mutationStatus.length}/${dialogChanges.length} applications`;
                }
                return 'Preparing to submit changes...';
              })()}
            </DevHubTypography>
          </DevHubBox>
        )}
      </DevHubDialogContent>

      <DevHubDialogActions>
        {isProcessingComplete && hasErrors && (
          <DevHubButton
            onClick={handleSubmit}
            color="secondary"
            variant="outlined"
          >
            Retry Failed
          </DevHubButton>
        )}

        <DevHubButton
          onClick={handleClose}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Close'}
        </DevHubButton>

        {!submissionStarted && (
          <DevHubButton
            onClick={handleSubmit}
            color="primary"
            variant="contained"
          >
            Submit Changes
          </DevHubButton>
        )}
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

export default DataLeadSubmissionReview;
